import qs from 'qs';
import React from 'react';
import { NotificationManager } from 'react-notifications';
import { useMutation, useQuery, useInfiniteQuery } from 'react-query';
import { EntityType } from 'shared/constants/entityTypes';
import { safeJsonParse } from 'shared/utils/functions';
import { useEntityDisplayName } from '../misc/fields';
import { axiosinstance } from 'shared/utils/api';
import { useAppStore } from 'App/store';
import { queryClient } from 'index';
import {
  getFieldConfig,
  mutateEnt,
  mutateEnts,
  postProcessPayload,
} from 'shared/utils/fields';

const ME_QUERY = 'me';
const baseUrl = '/v1/contact';

const refetchMe = () => queryClient.refetchQueries(ME_QUERY, { active: true });
export default refetchMe;

export function useMeQuery() {
  const fields = getFieldConfig(EntityType.CONTACT);
  const { data = {}, ...queryObject } = useQuery(ME_QUERY, () =>
    axiosinstance
      .get(baseUrl)
      .then(({ data: res }) => postProcessPayload(res.data, fields))
  );
  return [
    {
      ...data,
      preferences: {
        ...safeJsonParse(data?.preferences),
        ...data?.contact_customer_preferences,
      },
    },
    queryObject,
  ];
}

export function useContact(id, options = {}) {
  const {
    appState: { customerId },
  } = useAppStore();
  const fields = getFieldConfig(EntityType.CONTACT);
  const fetchContact = () =>
    axiosinstance
      .get(`/v1/customers/${customerId}/contacts/${id}`)
      .then(({ data: res }) => ({
        ...postProcessPayload(res.data, fields),
        role_ids: res.portal_roles?.map(r => r.id),
      }));

  const { data = {}, ...queryResult } = useQuery(
    ['contact', id],
    fetchContact,
    options
  );

  return { data, ...queryResult };
}

export function useContactsSave(id) {
  const {
    appState: { customerId },
  } = useAppStore();
  const entityType = EntityType.CONTACT;
  const [contactSingular] = useEntityDisplayName(entityType);
  const { mutateAsync, ...mutationObject } = useMutation(
    (payload) =>
      mutateEnts({
        payload,
        entityType,
        method: 'post',
        url: `/v1/customers/${customerId}/contacts`,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('contacts', {
          refetchInactive: true,
        });
        if (id) {
          queryClient.invalidateQueries(['contact', id], {
            refetchInactive: true,
          });
        }
        NotificationManager.success(`${contactSingular} successfully updated.`);
      },
    }
  );

  return [mutateAsync, mutationObject];
}

export function useContactPreferencesUpdate() {
  const { mutate, ...mutationObject } = useMutation(
    (data) =>
      axiosinstance
        .patch(`${baseUrl}/preferences`, data)
        .then((res) => res.data),
    {
      onSuccess: refetchMe,
    }
  );
  return [mutate, mutationObject];
}

export const useContactList = (
  urlParams = { page: 1, pageSize: 20 },
  filterParams = { portal_users_only: true }
) => {
  const params = React.useMemo(() => {
    return {
      page: urlParams.page,
      page_size: urlParams.pageSize,
      ...filterParams,
    };
  }, [urlParams, filterParams]);

  const fetchContact = async ({ pageParam = params.page }) => {
    const { data } = await axiosinstance.get(`${baseUrl}s`, {
      params: {
        ...params,
        page: pageParam,
      },
      paramsSerializer: (params_1) => {
        return qs.stringify(params_1, { arrayFormat: 'repeat' });
      },
    });
    return data;
  };

  const {
    data: contacts,
    fetchNextPage,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery('contacts', fetchContact, {
    getNextPageParam: (lastPage, pages) =>
      lastPage.page_number === lastPage.total_pages
        ? undefined
        : lastPage.page_number + 1,
  });

  return [
    contacts,
    { fetchNextPage, isFetching, hasNextPage, isFetchingNextPage },
  ];
};

export const usePortalUserInvite = ({
  contactId,
  resend = false,
}) => {
  const {
    appState: { customerId },
  } = useAppStore();
  const { mutateAsync, isLoading, isSuccess } = useMutation(
    () =>
      mutateEnt({
        entityType: EntityType.CONTACT,
        url: `/v1/customers/${customerId}/contacts/${contactId}/invite`,
        method: 'post',
        payload: { resend },
      }),
    {
      onSuccess: (result) => {
        NotificationManager.success(
          'The invite has been sent to the portal user email.'
        );
      },
    }
  );

  return [mutateAsync, { isLoading, isSuccess }];
};

export const useContactCustomerDelete = (contactId) => {
  const {
    appState: { customerId },
  } = useAppStore();
  const { mutateAsync, isLoading } = useMutation(
    () =>
      axiosinstance
        .delete(`/v1/customers/${customerId}/contacts/${contactId}`)
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.setQueryData(['contacts'], (data) => ({
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            data: page.data.filter((c) => c.id !== contactId),
          })),
        }));
      },
    }
  );

  return [mutateAsync, { isLoading }];
};
